import classNames from 'classnames';
import PropTypes from 'prop-types';
import IconButton from 'components/common/IconButton';
import { Button, Card, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import ReactFlagsSelect from 'react-flags-select';
import { RHFCheck, RHFControl } from 'components/common/form';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { COUNTRIES_LIST } from 'data/weraise-data/countries-data';
import Flex from 'components/common/Flex';
import { supabase } from 'lib/supabase';

const AddressInputs = ({
  // addNewAddress,
  deleteAddress,
  setValue,
  addresses,
  isEdit
}) => {
  const { control } = useFormContext();
  const [addressType, setAddressType] = useState(
    isEdit ? 'Non-UK_Address' : 'UK_Address'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [addressList, setAddressList] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search
  const debounceTimer = useRef(null);

  const fetchAddresses = useCallback(async query => {
    if (!query || query.length < 2) {
      setAddressList([]);
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('address-lookup', {
        body: { action: 'autocomplete', postCode: query }
      });

      if (error) throw error;
      setAddressList(data.suggestions || []);
      setShowDropdown(true);
    } catch (error) {
      console.error(error.message);
      setAddressList([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debouncing
  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer for debounced search
    debounceTimer.current = setTimeout(() => {
      fetchAddresses(value);
    }, 300);
  };

  const fetchAddressDetails = async addressId => {
    try {
      const { data, error } = await supabase.functions.invoke('address-lookup', {
        body: { action: 'get', addressId }
      });

      if (error) throw error;
      setSelectedAddress(data);
      setShowDropdown(false);
      setSearchQuery('');
      setAddressList([]);
    } catch (error) {
      console.error(error.message);
    }
  };

  const handleManualAddressEntry = () => {
    setAddressType('Non-UK_Address');
    setSelectedAddress(null);
    setShowDropdown(false);
    setAddressList([]);
    // Auto-set country to United Kingdom for manual UK address entry
    setValue('addresses', [
      {
        addressLine1: '',
        addressLine2: '',
        city: '',
        country: 'GB',
        postcode: '',
        isPrimary: true
      }
    ]);
  };

  const handleAddressClick = addressId => {
    fetchAddressDetails(addressId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (selectedAddress) {
      // Re-validate so "required" errors left from an earlier submit clear
      // once the lookup has filled the fields in
      setValue(
        'addresses',
        [
          {
            addressLine1: selectedAddress.line_1,
            addressLine2: selectedAddress.line_2,
            city: selectedAddress.town_or_city,
            country: 'GB',
            postcode: selectedAddress?.postcode,
            isPrimary: true
          }
        ],
        { shouldValidate: true, shouldDirty: true }
      );
    }
  }, [selectedAddress, setValue]);

  return (
    <Card className="mb-3">
      {addresses.map((address, index) => (
        <Card key={`address-${index}`} className="shadow-none">
          <Card.Header>
            <Card.Title className="fw-medium d-flex justify-content-between align-items-center">
              <span>Donor Address</span>
              {index !== 0 && (
                <IconButton
                  variant="light"
                  icon="trash"
                  className={classNames(
                    'mb-1 bg-transparent border-0 shadow-none',
                    { 'v-hidden': address.defaultAddress }
                  )}
                  iconAlign="left"
                  transform="shrink-3"
                  iconClassName="text-danger me-2"
                  onClick={() => deleteAddress(index)}
                  disabled={address.defaultAddress}
                >
                  Delete
                </IconButton>
              )}
            </Card.Title>
          </Card.Header>
          <Card.Body>
            <Flex direction="row" gap={2} className="mb-3">
              <Button
                variant={
                  addressType === 'UK_Address'
                    ? 'outline-primary'
                    : 'outline-secondary'
                }
                onClick={() => setAddressType('UK_Address')}
              >
                UK Address
              </Button>{' '}
              <Button
                variant={
                  addressType === 'Non-UK_Address'
                    ? 'outline-primary'
                    : 'outline-secondary'
                }
                onClick={() => setAddressType('Non-UK_Address')}
              >
                Non-UK Address
              </Button>{' '}
            </Flex>

            {addressType === 'UK_Address' && (
              <div className="position-relative mb-4">
                <Form.Label>Search Address or Postcode</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    ref={inputRef}
                    type="text"
                    placeholder="Start typing address or postcode..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onFocus={() => addressList.length > 0 && setShowDropdown(true)}
                  />
                  {isLoading && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                      <Spinner animation="border" size="sm" />
                    </div>
                  )}
                </div>

                {showDropdown && addressList.length > 0 && (
                  <ListGroup
                    ref={dropdownRef}
                    className="position-absolute w-100 shadow-sm"
                    style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                  >
                    {addressList.map(({ address, id }) => (
                      <ListGroup.Item
                        key={id}
                        action
                        onClick={() => handleAddressClick(id)}
                        className="cursor-pointer"
                        style={{ cursor: 'pointer' }}
                      >
                        {address}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}

                {searchQuery.length >= 2 && !isLoading && addressList.length === 0 && (
                  <div className="text-muted small mt-1">
                    No addresses found. Try a different search or enter manually.
                  </div>
                )}
              </div>
            )}

            {(addressType === 'Non-UK_Address' || selectedAddress) && (
              <>
                <Row className="g-3">
                  <Col md="12">
                    <RHFControl
                      name={`addresses.${index}.addressLine1`}
                      label="First Line Address"
                      placeholder="add Address Line 1"
                      required
                    />
                  </Col>
                  <Col md="12">
                    <RHFControl
                      name={`addresses.${index}.addressLine2`}
                      label="Second Line Address"
                      placeholder="add Address Line 2"
                    />
                  </Col>
                  <Col md="6">
                    <RHFControl
                      name={`addresses.${index}.city`}
                      label="City / Town"
                      placeholder="add City / Town"
                      required
                    />
                  </Col>
                  <Col md="6">
                    <RHFControl
                      name={`addresses.${index}.postcode`}
                      label="Post Code"
                      placeholder="add Zip/post Code"
                      required
                    />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="text-600">
                      Country <span className="text-danger">*</span>
                    </Form.Label>
                    <Row>
                      <Col md="6">
                        <Controller
                          name={`addresses.${index}.country`}
                          control={control}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ReactFlagsSelect
                                selected={
                                  selectedAddress?.country
                                    ? 'GB'
                                    : field.value
                                }
                                onSelect={code => {
                                  field.onChange(code);
                                }}
                                className={`react-select${error ? ' is-invalid' : ''}`}
                                countries={Object.keys(COUNTRIES_LIST)}
                                selectButtonClassName={`py-0${error ? ' border-danger' : ''}`}
                                selectedSize={17.3}
                                optionsSize={17.3}
                              />
                              {error && (
                                <Form.Control.Feedback
                                  className="d-block"
                                  type="invalid"
                                >
                                  {error.message}
                                </Form.Control.Feedback>
                              )}
                            </>
                          )}
                        />
                      </Col>
                      <Col md={6} className="mt-1">
                        <RHFCheck
                          name={`addresses.${index}.isPrimary`}
                          label="Primary"
                          onChange={e => {
                            setValue(
                              `addresses.${index}.isPrimary`,
                              e.target.checked
                            );
                          }}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>

          {index !== addresses.length - 1 && <hr className="m-0" />}
        </Card>
      ))}

      {addressType === 'UK_Address' && (
        <Button
          variant="link"
          className="text-primary mx-auto"
          onClick={handleManualAddressEntry}
        >
          Enter Address Manually{' '}
        </Button>
      )}

      {/* <Flex direction="column" alignItems="start" className="m-4">
        <Button variant="primary" onClick={addNewAddress}>
          Add New Address
        </Button>
      </Flex> */}
    </Card>
  );
};

AddressInputs.propTypes = {
  addNewAddress: PropTypes.func.isRequired,
  deleteAddress: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  addresses: PropTypes.arrayOf(PropTypes.object).isRequired,
  isEdit: PropTypes.bool
};

export default AddressInputs;
